import { instrumentAnswerKey } from '@seen/core/observation-logic/instruments/records'
import { panelAnswers } from '@seen/core/observation-logic/noteCapture'
import type { ObservationValue } from '@seen/core/observation-logic/value'
import { panelTemplate } from '@seen/core/terminology/instruments/loincPanels'
import { instrumentShortName } from '@seen/core/terminology/instruments/registry'
import type {
  InstrumentItem,
  InstrumentTemplate,
} from '@seen/core/terminology/instruments/types'
import type { Editor } from '@tiptap/core'
import { useEffect, useMemo, useRef, useState } from 'react'
import type { InstrumentCapture } from '#src/components/rich-text-editor/ObservationNode/panel/useInstrumentCapture.ts'

export interface CarriedAnswers {
  // Other instruments captured in this note, named by group id.
  notePanels: ReadonlyMap<string, string>
  // The answers filled in on opening, and where they came from.
  carried: { keys: string[]; from: string[] } | null
  undoCarried: () => void
}

interface UseCarriedAnswersOptions {
  editor: Editor
  panelId: string | null | undefined
  template: InstrumentTemplate
  capture: InstrumentCapture
  visibleItems: readonly InstrumentItem[]
  open: boolean
  disabled: boolean
  // Other panels' records are not written yet: their answers are read from
  // the panels themselves.
  deferred?: boolean
}

// Answers from this visit (another instrument in this note, or today) fill
// the rows still empty when the grid first opens, as real answers, and can
// be undone together.
export function useCarriedAnswers({
  editor,
  panelId,
  template,
  capture,
  visibleItems,
  open,
  disabled,
  deferred = false,
}: UseCarriedAnswersOptions): CarriedAnswers {
  // The only source answers carry over from, and how an earlier answer's
  // source is named. Another capture of this same instrument is not one.
  const doc = editor.state.doc
  const { notePanels, noteAnswers } = useMemo(() => {
    const found = new Map<string, string>()
    // Each other panel's held answers, in note order.
    const held: {
      name: string
      answers: Record<string, ObservationValue | null | undefined>
    }[] = []
    doc.descendants((node) => {
      if (
        node.type.name === 'observation-panel' &&
        node.attrs.groupId &&
        node.attrs.id !== panelId &&
        node.attrs.templateId !== template.id
      ) {
        const other = panelTemplate(node.attrs)
        found.set(node.attrs.groupId, other ? instrumentShortName(other) : '')
        const answers = panelAnswers(node.attrs)
        if (answers) {
          held.push({ name: other ? instrumentShortName(other) : '', answers })
        }
      }
      return node.type.name !== 'observation-panel'
    })
    return { notePanels: found, noteAnswers: held }
  }, [doc, panelId, template.id])

  const [carried, setCarried] = useState<CarriedAnswers['carried']>(null)
  const carriedOnce = useRef(false)
  useEffect(() => {
    if (!open || !capture.ready || disabled || carriedOnce.current) {
      return
    }
    carriedOnce.current = true
    const keys: string[] = []
    const from = new Set<string>()
    for (const item of visibleItems) {
      const key = instrumentAnswerKey(item.coding)
      if (capture.answers[key]) {
        continue
      }
      if (deferred) {
        const source = noteAnswers.find((panel) => panel.answers[key])
        const value = source?.answers[key]
        if (source && value) {
          capture.answer(item, value)
          keys.push(key)
          from.add(source.name || 'this note')
        }
        continue
      }
      const prior = capture.priors[key]
      if (prior?.groupId && notePanels.has(prior.groupId)) {
        capture.answer(item, prior.value)
        keys.push(key)
        from.add(notePanels.get(prior.groupId) || 'this note')
      }
    }
    if (keys.length) {
      setCarried({ keys, from: [...from] })
    }
    // Runs once (carriedOnce): the rest is read as it stands then.
  }, [open, capture, disabled, visibleItems, notePanels, noteAnswers, deferred])

  const undoCarried = (): void => {
    for (const item of visibleItems) {
      if (carried?.keys.includes(instrumentAnswerKey(item.coding))) {
        capture.answer(item, null)
      }
    }
    setCarried(null)
  }

  return { notePanels, carried, undoCarried }
}
